import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Typography,
  Tag,
  Space,
  message,
  Card,
  Tooltip,
  Spin,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  LinkOutlined,
  BarChartOutlined,
  LineChartOutlined,
  DatabaseOutlined,
  CloudOutlined,
  FolderOutlined,
  FlagOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/backend/api";

const { Title, Text } = Typography;

const Performance = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [objectivesLoading, setObjectivesLoading] = useState({});
  const [projectObjectives, setProjectObjectives] = useState({});
  const [viewMode, setViewMode] = useState("localStorage"); // "localStorage" or "connected"
  const [localStorageItems, setLocalStorageItems] = useState([]);

  const loadLocalStorageItems = useCallback(() => {
    try {
      const storedProjects = JSON.parse(
        localStorage.getItem("connectedProjects") || "[]",
      );
      const storedObjectives = JSON.parse(
        localStorage.getItem("connectedProjectObjectives") || "[]",
      );

      // Combine and mark items with their type
      const combinedItems = [
        ...storedProjects.map((item) => ({
          ...item,
          itemType: "project",
          typeLabel: "Project",
        })),
        ...storedObjectives.map((item) => ({
          ...item,
          itemType: "objective",
          typeLabel: "Project Objective",
          parentProjectId: item.projectId,
        })),
      ];

      setLocalStorageItems(combinedItems);
    } catch (error) {
      console.error("Error loading localStorage items:", error);
      message.error("Failed to load items from local storage");
      setLocalStorageItems([]);
    }
  }, []);

  useEffect(() => {
    loadLocalStorageItems();
  }, [loadLocalStorageItems]);

  const fetchAllProjects = async () => {
    setLoading(true);
    try {
      const data = await api.get("/project/connected/all");
      setProjects(Array.isArray(data) ? data : []);
      message.success(
        `Loaded ${Array.isArray(data) ? data.length : 0} projects`,
      );
    } catch (error) {
      console.error("Error fetching projects:", error);
      message.error("Failed to fetch projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleView = () => {
    if (viewMode === "localStorage") {
      setViewMode("connected");
      fetchAllProjects();
    } else {
      setViewMode("localStorage");
      loadLocalStorageItems();
    }
  };

  const searchProjects = async (key) => {
    if (!key || key.trim().length === 0) {
      message.warning("Please enter a search term");
      return;
    }

    setLoading(true);
    try {
      const data = await api.get(
        `/project/connected/search/${encodeURIComponent(key.trim())}`,
      );
      setProjects(Array.isArray(data) ? data : []);
      message.success(
        `Found ${Array.isArray(data) ? data.length : 0} project(s)`,
      );
    } catch (error) {
      console.error("Error searching projects:", error);
      message.error("Failed to search projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchKey.trim()) {
      searchProjects(searchKey);
    } else {
      fetchAllProjects();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const handleLinkToLocalStorage = useCallback(
    (project) => {
      try {
        const existingProjects = JSON.parse(
          localStorage.getItem("connectedProjects") || "[]",
        );
        const projectToSave = {
          ...project,
          savedAt: new Date().toISOString(),
        };

        const existingIndex = existingProjects.findIndex(
          (p) => p.id === project.id,
        );

        if (existingIndex >= 0) {
          existingProjects[existingIndex] = projectToSave;
          message.success("Project updated in local storage");
        } else {
          existingProjects.push(projectToSave);
          message.success("Project saved to local storage");
        }

        localStorage.setItem(
          "connectedProjects",
          JSON.stringify(existingProjects),
        );

        // Refresh localStorage view if currently viewing it
        if (viewMode === "localStorage") {
          loadLocalStorageItems();
        }
      } catch (error) {
        console.error("Error saving to localStorage:", error);
        message.error("Failed to save project to local storage");
      }
    },
    [viewMode, loadLocalStorageItems],
  );

  const handleGoToPerformanceReport = useCallback(
    (project) => {
      navigate(`/performance/report?projectId=${project.id}`);
      message.info(
        `Loading performance report for ${project.name || project.id}`,
      );
    },
    [navigate],
  );

  const handleGoToForecast = useCallback(
    (project) => {
      // Assuming forecast tool is in a separate app or route
      // You can update this URL based on your actual forecast tool location
      const forecastUrl = `/forecast?projectId=${project.id}`;
      navigate(forecastUrl);
      message.info(`Loading forecast for ${project.name || project.id}`);
    },
    [navigate],
  );

  const handleLinkObjectiveToLocalStorage = useCallback(
    (objective, projectId) => {
      try {
        const existingObjectives = JSON.parse(
          localStorage.getItem("connectedProjectObjectives") || "[]",
        );
        const objectiveToSave = {
          ...objective,
          projectId,
          savedAt: new Date().toISOString(),
        };

        const existingIndex = existingObjectives.findIndex(
          (o) => o.id === objective.id,
        );

        if (existingIndex >= 0) {
          existingObjectives[existingIndex] = objectiveToSave;
          message.success("Project objective updated in local storage");
        } else {
          existingObjectives.push(objectiveToSave);
          message.success("Project objective saved to local storage");
        }

        localStorage.setItem(
          "connectedProjectObjectives",
          JSON.stringify(existingObjectives),
        );

        // Refresh localStorage view if currently viewing it
        if (viewMode === "localStorage") {
          loadLocalStorageItems();
        }
      } catch (error) {
        console.error("Error saving objective to localStorage:", error);
        message.error("Failed to save project objective to local storage");
      }
    },
    [viewMode, loadLocalStorageItems],
  );

  const handleGoToObjectivePerformanceReport = useCallback(
    (objective) => {
      navigate(`/performance/report?objectiveId=${objective.id}`);
      message.info(
        `Loading performance report for ${objective.name || objective.id}`,
      );
    },
    [navigate],
  );

  const handleGoToObjectiveForecast = useCallback(
    (objective) => {
      const forecastUrl = `/forecast?objectiveId=${objective.id}`;
      navigate(forecastUrl);
      message.info(`Loading forecast for ${objective.name || objective.id}`);
    },
    [navigate],
  );

  const fetchProjectObjectives = async (projectId) => {
    if (projectObjectives[projectId]) {
      return; // Already loaded
    }

    setObjectivesLoading((prev) => ({ ...prev, [projectId]: true }));
    try {
      const data = await api.get(`/project/connected/${projectId}/objectives`);
      setProjectObjectives((prev) => ({
        ...prev,
        [projectId]: Array.isArray(data) ? data : [],
      }));
    } catch (error) {
      console.error("Error fetching project objectives:", error);
      setProjectObjectives((prev) => ({
        ...prev,
        [projectId]: [],
      }));
      message.error("Failed to load project objectives");
    } finally {
      setObjectivesLoading((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record.id]);
      fetchProjectObjectives(record.id);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.id));
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 120,
        ellipsis: true,
        render: (text) => (
          <span style={{ fontFamily: "monospace", fontSize: "0.9em" }}>
            {text || "N/A"}
          </span>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 250,
        ellipsis: true,
        sorter: (a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        },
        render: (text) => (
          <span style={{ fontWeight: 500 }}>{text || "N/A"}</span>
        ),
      },
      {
        title: "Short Name",
        dataIndex: "short_project_name_c",
        key: "short_project_name_c",
        width: 150,
        ellipsis: true,
      },
      {
        title: "Client",
        dataIndex: "client_name_c",
        key: "client_name_c",
        width: 180,
        ellipsis: true,
        render: (text) => text || "N/A",
      },
      {
        title: "Status",
        dataIndex: "project_status_c",
        key: "project_status_c",
        width: 120,
        filters: [
          { text: "Open", value: "Open" },
          { text: "Closed", value: "Closed" },
          { text: "Draft", value: "Draft" },
        ],
        onFilter: (value, record) => record.project_status_c === value,
        render: (text) => {
          if (!text) return "N/A";
          const colorMap = {
            Open: "green",
            Closed: "red",
            Draft: "blue",
          };
          return <Tag color={colorMap[text] || "default"}>{text}</Tag>;
        },
      },
      {
        title: "Type",
        dataIndex: "project_type_c",
        key: "project_type_c",
        width: 120,
        ellipsis: true,
        render: (text) => text || "N/A",
      },
      {
        title: "Description",
        dataIndex: "project_short_description_c",
        key: "project_short_description_c",
        width: 300,
        ellipsis: {
          showTitle: true,
        },
        render: (text) => (
          <span style={{ color: "#666" }}>{text || "N/A"}</span>
        ),
      },
      {
        title: "Created Date",
        dataIndex: "created_date",
        key: "created_date",
        width: 130,
        sorter: (a, b) => {
          const dateA = new Date(a.created_date || 0);
          const dateB = new Date(b.created_date || 0);
          return dateA - dateB;
        },
        render: (text) => formatDate(text),
      },
      {
        title: "Actions",
        key: "actions",
        width: 150,
        fixed: "right",
        render: (_, record) => (
          <Space size="small">
            <Tooltip title="Link to Local Storage">
              <Button
                type="text"
                icon={<LinkOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLinkToLocalStorage(record);
                }}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Go to Performance Report">
              <Button
                type="text"
                icon={<BarChartOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGoToPerformanceReport(record);
                }}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Go to Forecast">
              <Button
                type="text"
                icon={<LineChartOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGoToForecast(record);
                }}
                size="small"
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [handleLinkToLocalStorage, handleGoToPerformanceReport, handleGoToForecast],
  );

  const getLocalStorageColumns = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 120,
        ellipsis: true,
        render: (text) => (
          <span style={{ fontFamily: "monospace", fontSize: "0.9em" }}>
            {text || "N/A"}
          </span>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 250,
        ellipsis: true,
        sorter: (a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        },
        render: (text, record) => {
          const isProject = record.itemType === "project";
          // For objectives, try to get objective name
          const displayName =
            record.itemType === "objective"
              ? record.project_objective_name_c || text
              : text;
          return (
            <Space>
              {isProject ? (
                <FolderOutlined style={{ color: "#1890ff" }} />
              ) : (
                <FlagOutlined style={{ color: "#722ed1" }} />
              )}
              <span style={{ fontWeight: 500 }}>{displayName || "N/A"}</span>
            </Space>
          );
        },
      },
      {
        title: "Status",
        key: "status",
        width: 120,
        render: (_, record) => {
          const status =
            record.itemType === "project"
              ? record.project_status_c
              : record.status_c;
          if (!status) return <Tag color="default">N/A</Tag>;

          const statusLower = String(status).toLowerCase().trim();
          const colorMap = {
            active: "green",
            inactive: "red",
            completed: "blue",
            done: "blue",
            draft: "orange",
            "on hold": "purple",
            hold: "purple",
            cancelled: "red",
            canceled: "red",
            pending: "gold",
            "in progress": "cyan",
            "in-progress": "cyan",
            progress: "cyan",
            closed: "default",
            open: "green",
          };
          const color = colorMap[statusLower] || "default";
          return <Tag color={color}>{status}</Tag>;
        },
      },
      {
        title: "Description",
        key: "description",
        width: 300,
        ellipsis: {
          showTitle: true,
        },
        render: (_, record) => {
          const description =
            record.itemType === "project"
              ? record.project_short_description_c
              : record.project_objective_description_c;
          return <span style={{ color: "#666" }}>{description || "N/A"}</span>;
        },
      },
      {
        title: "Saved Date",
        dataIndex: "savedAt",
        key: "savedAt",
        width: 130,
        sorter: (a, b) => {
          const dateA = new Date(a.savedAt || 0);
          const dateB = new Date(b.savedAt || 0);
          return dateA - dateB;
        },
        render: (text) => formatDate(text),
      },
      {
        title: "Actions",
        key: "actions",
        width: 150,
        fixed: "right",
        render: (_, record) => {
          const isProject = record.itemType === "project";
          return (
            <Space size="small">
              <Tooltip title="Go to Performance Report">
                <Button
                  type="text"
                  icon={<BarChartOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isProject) {
                      handleGoToPerformanceReport(record);
                    } else {
                      handleGoToObjectivePerformanceReport(record);
                    }
                  }}
                  size="small"
                />
              </Tooltip>
              <Tooltip title="Go to Forecast">
                <Button
                  type="text"
                  icon={<LineChartOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isProject) {
                      handleGoToForecast(record);
                    } else {
                      handleGoToObjectiveForecast(record);
                    }
                  }}
                  size="small"
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ],
    [
      handleGoToPerformanceReport,
      handleGoToForecast,
      handleGoToObjectivePerformanceReport,
      handleGoToObjectiveForecast,
    ],
  );

  const getObjectiveColumns = useCallback(
    (projectId) => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 120,
        ellipsis: true,
        render: (text) => (
          <span style={{ fontFamily: "monospace", fontSize: "0.9em" }}>
            {text || "N/A"}
          </span>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 250,
        ellipsis: true,
        sorter: (a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        },
        render: (text) => (
          <span style={{ fontWeight: 500 }}>{text || "N/A"}</span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status_c",
        key: "status_c",
        width: 120,
        filters: [
          { text: "Active", value: "Active" },
          { text: "Inactive", value: "Inactive" },
          { text: "Completed", value: "Completed" },
          { text: "Draft", value: "Draft" },
          { text: "On Hold", value: "On Hold" },
          { text: "Cancelled", value: "Cancelled" },
        ],
        onFilter: (value, record) => record.status_c === value,
        render: (text) => {
          if (!text) return <Tag color="default">N/A</Tag>;

          // Case-insensitive status matching with comprehensive color mapping
          const statusLower = String(text).toLowerCase().trim();
          const colorMap = {
            active: "green",
            inactive: "red",
            completed: "blue",
            done: "blue",
            draft: "blue",
            "on hold": "purple",
            hold: "purple",
            cancelled: "red",
            canceled: "red",
            pending: "gold",
            "in progress": "cyan",
            "in-progress": "cyan",
            progress: "cyan",
            closed: "red",
            open: "green",
          };

          // Find matching color (case-insensitive)
          const color = colorMap[statusLower] || "default";

          return <Tag color={color}>{text}</Tag>;
        },
      },
      {
        title: "Description",
        dataIndex: "project_objective_description_c",
        key: "project_objective_description_c",
        width: 300,
        ellipsis: {
          showTitle: true,
        },
        render: (text) => (
          <span style={{ color: "#666" }}>{text || "N/A"}</span>
        ),
      },
      {
        title: "Created Date",
        dataIndex: "created_date",
        key: "created_date",
        width: 130,
        sorter: (a, b) => {
          const dateA = new Date(a.created_date || 0);
          const dateB = new Date(b.created_date || 0);
          return dateA - dateB;
        },
        render: (text) => formatDate(text),
      },
      {
        title: "Actions",
        key: "actions",
        width: 150,
        fixed: "right",
        render: (_, record) => (
          <Space size="small">
            <Tooltip title="Link to Local Storage">
              <Button
                type="text"
                icon={<LinkOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLinkObjectiveToLocalStorage(record, projectId);
                }}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Go to Performance Report">
              <Button
                type="text"
                icon={<BarChartOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGoToObjectivePerformanceReport(record);
                }}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Go to Forecast">
              <Button
                type="text"
                icon={<LineChartOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGoToObjectiveForecast(record);
                }}
                size="small"
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [
      handleLinkObjectiveToLocalStorage,
      handleGoToObjectivePerformanceReport,
      handleGoToObjectiveForecast,
    ],
  );

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Card
        style={{
          borderRadius: "12px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        <div className="mb-6">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 24,
            }}
          >
            <Title level={2} style={{ margin: 0 }}>
              {viewMode === "localStorage" ? "Recents" : "Connected Projects"}
            </Title>
            <Space>
              {viewMode === "localStorage" && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadLocalStorageItems}
                  title="Refresh recent items"
                >
                  Refresh
                </Button>
              )}
              <Button
                type={viewMode === "localStorage" ? "default" : "primary"}
                icon={
                  viewMode === "localStorage" ? (
                    <CloudOutlined />
                  ) : (
                    <DatabaseOutlined />
                  )
                }
                onClick={handleToggleView}
              >
                {viewMode === "localStorage"
                  ? "Show Connected Projects"
                  : "Show Recents"}
              </Button>
            </Space>
          </div>

          {viewMode === "connected" && (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Space.Compact style={{ width: "100%", maxWidth: 600 }}>
                <Input
                  placeholder="Search by project name or ID..."
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  onKeyPress={handleKeyPress}
                  allowClear
                  style={{ flex: 1 }}
                  prefix={<SearchOutlined />}
                />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                >
                  Search
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchAllProjects}
                  loading={loading}
                >
                  Load All
                </Button>
              </Space.Compact>
            </Space>
          )}
        </div>

        {viewMode === "localStorage" ? (
          <Table
            rowKey={(record) =>
              `${record.itemType}-${record.id}` || Math.random()
            }
            dataSource={localStorageItems}
            columns={getLocalStorageColumns}
            scroll={{ x: 1200, y: "calc(100vh - 380px)" }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50", "100"],
              onShowSizeChange: (current, size) => setPageSize(size),
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} item${total !== 1 ? "s" : ""}`,
              showQuickJumper: true,
            }}
            locale={{
              emptyText:
                "No recent items found. Link projects or objectives to see them here.",
            }}
          />
        ) : (
          <Table
            loading={loading}
            rowKey={(record) => record.id || Math.random()}
            dataSource={projects}
            columns={columns}
            scroll={{ x: 1400, y: "calc(100vh - 380px)" }}
            expandable={{
              expandedRowKeys,
              onExpand: handleExpand,
              expandedRowRender: (record) => {
                const objectives = projectObjectives[record.id] || [];
                const isLoading = objectivesLoading[record.id];

                return (
                  <div style={{ padding: "16px", background: "#fafafa" }}>
                    <Text
                      strong
                      style={{
                        fontSize: "14px",
                        marginBottom: "12px",
                        display: "block",
                      }}
                    >
                      Project Objectives ({objectives.length})
                    </Text>
                    {isLoading ? (
                      <div style={{ textAlign: "center", padding: "20px" }}>
                        <Spin size="small" />
                        <div style={{ marginTop: "8px", color: "#666" }}>
                          Loading objectives...
                        </div>
                      </div>
                    ) : objectives.length === 0 ? (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#999",
                        }}
                      >
                        No objectives found for this project
                      </div>
                    ) : (
                      <Table
                        dataSource={objectives}
                        columns={getObjectiveColumns(record.id)}
                        rowKey={(obj) => obj.id || Math.random()}
                        pagination={{
                          pageSize: 5,
                          showSizeChanger: true,
                          pageSizeOptions: ["5", "10", "20"],
                          showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} objective${
                              total !== 1 ? "s" : ""
                            }`,
                        }}
                        size="small"
                        scroll={{ x: 1000 }}
                        locale={{
                          emptyText: "No objectives found",
                        }}
                      />
                    )}
                  </div>
                );
              },
              rowExpandable: () => true,
            }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50", "100"],
              onShowSizeChange: (current, size) => setPageSize(size),
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} project${
                  total !== 1 ? "s" : ""
                }`,
              showQuickJumper: true,
            }}
            locale={{
              emptyText:
                "No projects found. Click 'Load All' to fetch projects or use search to find specific projects.",
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default Performance;
